/** 243-ways win evaluation algorithm for Rush Fever 7s Deluxe */

import type { WinResult } from './SpinResultGenerator';
import { PAY_TABLE, PAY_TABLE_MAP, WILD_SYMBOL, RUSH_SYMBOL, SCATTER_SYMBOL } from '../config/PayTable';
import { Logger } from '../utils/Logger';

const logger = new Logger('WinEvaluator');

export interface EvaluationContext {
  /** Whether this is a base game spin (6 reels) or free game (5 reels) */
  isBaseGame: boolean;
  /** Total bet amount for this spin */
  totalBet: number;
}

/**
 * Evaluates wins using the 243-ways-to-win algorithm.
 *
 * Grid format: grid[reelIndex][rowIndex] = symbolId
 * Each reel has 3 visible rows (indices 0, 1, 2).
 *
 * Algorithm:
 * - For each paying symbol (excluding WILD, Rush):
 *   1. Start from reel 1 (index 0). Count matching symbols (symbol + WILD) on that reel.
 *      If count = 0 on reel 1, skip this symbol entirely.
 *   2. Move to reel 2 (index 1). Count matching (symbol + WILD).
 *      If count = 0, streak ends.
 *   3. Continue through remaining reels.
 *   4. Streak = number of consecutive reels with at least 1 match.
 *   5. Ways = product of match counts per reel in the streak.
 *   6. Win = payTable[symbol][streakLength] * ways * betPerWay
 *
 * - WILD substitutes for all symbols EXCEPT SCATTER and Rush.
 * - WILD appears on reels 2, 3, 4 ONLY (indices 1, 2, 3).
 *   So on reel 1 (index 0), reel 5 (index 4), reel 6 (index 5), only the symbol itself counts.
 *
 * - SCATTER: count across ALL reels (not left-to-right).
 *   If count >= 3: win = payTable[SCATTER][count] * totalBet
 */
export class WinEvaluator {

  /**
   * Evaluate all wins on the given grid.
   * @param grid grid[reelIndex][rowIndex] = symbolId
   * @param context evaluation context with bet info
   * @returns array of winning results
   */
  static evaluate(grid: string[][], context: EvaluationContext): WinResult[] {
    const wins: WinResult[] = [];
    const reelCount = grid.length;
    const betPerWay = context.totalBet / 243;

    logger.debug(`Evaluating grid: ${reelCount} reels, totalBet=${context.totalBet}, betPerWay=${betPerWay.toFixed(6)}`);

    // Evaluate each regular paying symbol using 243-ways left-to-right
    for (const entry of PAY_TABLE) {
      if (entry.symbol === SCATTER_SYMBOL) continue; // Scatter evaluated separately

      const symbol = entry.symbol;
      let streakLength = 0;
      let totalWays = 1;
      const reelMatchPositions: [number, number][][] = []; // per-reel positions

      for (let reelIdx = 0; reelIdx < reelCount; reelIdx++) {
        const reelSymbols = grid[reelIdx];
        const matchPositions: [number, number][] = [];

        for (let rowIdx = 0; rowIdx < reelSymbols.length; rowIdx++) {
          const sym = reelSymbols[rowIdx];
          if (sym === symbol) {
            matchPositions.push([reelIdx, rowIdx]);
          } else if (sym === WILD_SYMBOL && symbol !== SCATTER_SYMBOL && symbol !== RUSH_SYMBOL) {
            // WILD substitutes on reels 2,3,4 (indices 1,2,3) — but the grid
            // already contains what's on the reel, so if a WILD is present
            // it's already on a valid reel. We just count it.
            matchPositions.push([reelIdx, rowIdx]);
          }
        }

        if (matchPositions.length === 0) {
          // No matches on this reel — streak ends
          break;
        }

        streakLength++;
        totalWays *= matchPositions.length;
        reelMatchPositions.push(matchPositions);
      }

      // Check if this streak length qualifies for a payout
      if (streakLength >= entry.minStreak) {
        const payoutPerWay = entry.payouts[streakLength - 1] ?? 0;
        if (payoutPerWay > 0) {
          const totalPayout = payoutPerWay * totalWays * betPerWay;

          // Flatten positions for the win result
          const allPositions: [number, number][] = [];
          for (const reelPositions of reelMatchPositions) {
            allPositions.push(...reelPositions);
          }

          wins.push({
            symbol,
            streakLength,
            ways: totalWays,
            coins: payoutPerWay * totalWays,
            positions: allPositions,
          });

          logger.debug(
            `Win: ${symbol} x${streakLength} (${totalWays} ways) = ${totalPayout.toFixed(4)} ` +
            `[payPerWay=${payoutPerWay}, betPerWay=${betPerWay.toFixed(6)}]`
          );
        }
      }
    }

    // Evaluate SCATTER (count across ALL reels, not left-to-right)
    const scatterWin = WinEvaluator.evaluateScatter(grid, context.totalBet);
    if (scatterWin) {
      wins.push(scatterWin);
    }

    if (wins.length > 0) {
      const totalWinAmount = wins.reduce((sum, w) => {
        if (w.symbol === SCATTER_SYMBOL) {
          // Scatter coins already represent the full payout multiplied by totalBet
          return sum + w.coins * (context.totalBet / 243); // No — scatter uses totalBet directly
        }
        return sum + w.coins * (context.totalBet / 243);
      }, 0);
      logger.info(`Total wins: ${wins.length} combinations`);

      // Log individual win details
      for (const w of wins) {
        logger.info(`  ${w.symbol} x${w.streakLength}: ${w.ways} ways, ${w.coins} coins`);
      }
    }

    return wins;
  }

  /**
   * Calculate the total monetary win from win results.
   * Regular symbols: coins * betPerWay
   * Scatter: coins * totalBet (coins already contains the payout multiplier)
   */
  static calculateTotalWin(wins: WinResult[], totalBet: number): number {
    const betPerWay = totalBet / 243;
    let total = 0;

    for (const win of wins) {
      if (win.symbol === SCATTER_SYMBOL) {
        // Scatter: payTable value * totalBet. coins = payTable[SCATTER][count]
        total += win.coins * totalBet;
      } else {
        // Regular: coins = payPerWay * ways. Multiply by betPerWay.
        total += win.coins * betPerWay;
      }
    }

    return total;
  }

  /**
   * Evaluate scatter wins across the entire grid.
   * Scatter pays based on total count (not left-to-right).
   * If count >= 3: win = payTable[SCATTER][count] * totalBet
   */
  private static evaluateScatter(grid: string[][], totalBet: number): WinResult | null {
    const scatterEntry = PAY_TABLE_MAP.get(SCATTER_SYMBOL);
    if (!scatterEntry) return null;

    const positions: [number, number][] = [];
    for (let reelIdx = 0; reelIdx < grid.length; reelIdx++) {
      for (let rowIdx = 0; rowIdx < grid[reelIdx].length; rowIdx++) {
        if (grid[reelIdx][rowIdx] === SCATTER_SYMBOL) {
          positions.push([reelIdx, rowIdx]);
        }
      }
    }

    const count = positions.length;
    if (count >= scatterEntry.minStreak) {
      // Scatter payouts indexed [1-match..6-match], so index = count - 1
      const payoutMultiplier = scatterEntry.payouts[count - 1] ?? 0;
      if (payoutMultiplier > 0) {
        logger.debug(
          `Scatter win: ${count} scatters, multiplier=${payoutMultiplier}, ` +
          `payout=${payoutMultiplier * totalBet}`
        );

        return {
          symbol: SCATTER_SYMBOL,
          streakLength: count,
          ways: 1,
          coins: payoutMultiplier, // Store the multiplier; actual payout = coins * totalBet
          positions,
        };
      }
    }

    return null;
  }

  /** Count scatter symbols across the entire grid */
  static countScatters(grid: string[][]): number {
    let count = 0;
    for (const reel of grid) {
      for (const sym of reel) {
        if (sym === SCATTER_SYMBOL) count++;
      }
    }
    return count;
  }

  /** Count rush symbols across the entire grid */
  static countRush(grid: string[][]): number {
    let count = 0;
    for (const reel of grid) {
      for (const sym of reel) {
        if (sym === RUSH_SYMBOL) count++;
      }
    }
    return count;
  }

  /** Check if any WILD symbol is present on the grid */
  static hasWild(grid: string[][]): boolean {
    for (const reel of grid) {
      for (const sym of reel) {
        if (sym === WILD_SYMBOL) return true;
      }
    }
    return false;
  }
}
